/**
 * ApexOutletTile — read-only status display for one Apex outlet.
 *
 * Layout (compact, single-row):
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ ▌ DT_Fan_On   [LIFE SUPPORT]  ●ON  AUTO  ▓▓▓░░ 65%               │
 *   └──────────────────────────────────────────────────────────────────┘
 *     │             │               │     │    │
 *     │             │               │     │    └ intensity bar (only when set)
 *     │             │               │     └ mode badge: AUTO / MANUAL / TABLE
 *     │             │               └ ON/OFF state badge (live dot when ON)
 *     │             └ safety-class badge (blocked = life support hardware)
 *     └ left accent stripe: amber for MANUAL, sky for TABLE, none for AUTO
 *
 * Monitor-only build: this app CANNOT write to the Apex. There are no
 * toggle buttons, no confirm modals, and no advanced-mode gating. The
 * safety class from `classifyOutlet` is purely display metadata — it
 * colors/badges life-support outlets so they stand out at a glance.
 *
 * `mode` is the *display* mode (AUTO/MANUAL/TABLE) — TABLE is derived
 * from `rawState === 'TBL'` (see `displayOutletMode`). It's purely a UI
 * convenience; the underlying snapshot only knows AUTO/MANUAL.
 *
 * The full `rawState` (e.g. `AUTO`, `OFF`, `TBL`, `100`) is exposed in
 * a `title` tooltip so power users can sanity-check what the Apex is
 * actually reporting.
 */
import clsx from 'clsx';

import { Badge, type BadgeVariant } from './Badge';
import { displayOutletMode, type DisplayOutletMode } from '../lib/apex-grouping';
import { classifyOutlet } from '../lib/outlet-safety';
import type { ApexOutletSnapshot } from '../lib/types';

export interface ApexOutletTileProps {
  outlet: ApexOutletSnapshot;
  className?: string;
}

const MODE_VARIANT: Record<DisplayOutletMode, BadgeVariant> = {
  AUTO: 'info',
  MANUAL: 'warn',
  TABLE: 'info',
};

/**
 * Left-edge accent stripe per display mode. AUTO stays unaccented to keep
 * the dashboard quiet at a glance; MANUAL and TABLE both indicate the
 * outlet is *not* being driven by normal rules and warrant a small visual
 * nudge.
 */
const MODE_ACCENT: Record<DisplayOutletMode, string> = {
  AUTO: '',
  MANUAL: 'border-l-2 border-amber-500/60',
  TABLE: 'border-l-2 border-sky-500/60',
};

/**
 * UI-only annotation: matches Apex DOS module outlet names like
 * `DOS_A`, `DOS_B`, `DOS_C`, `DOS_D`. Used to render a small "DOS"
 * chip on the tile so operators can see at a glance which outlets are
 * fronting an Apex dosing pump.
 */
export function isApexDosOutlet(name: string): boolean {
  return /^DOS_[A-Z]$/i.test(name);
}

export function ApexOutletTile({ outlet, className }: ApexOutletTileProps) {
  const mode = displayOutletMode(outlet);
  const isOn = outlet.state === 'ON';
  const safety = classifyOutlet(outlet.name);
  const intensityPct =
    outlet.intensity !== null && Number.isFinite(outlet.intensity)
      ? Math.max(0, Math.min(100, Math.round(outlet.intensity)))
      : null;

  return (
    <div
      data-testid={`apex-outlet-tile-${outlet.name}`}
      data-mode={mode}
      data-state={outlet.state}
      data-safety={safety}
      title={outlet.rawState}
      className={clsx(
        'flex items-center gap-3 rounded-lg bg-reef-900/40 px-3 py-2',
        'border border-reef-700/40',
        MODE_ACCENT[mode],
        className,
      )}
    >
      <span className="flex-1 truncate font-mono text-xs text-reef-100" title={outlet.name}>
        {outlet.name}
      </span>
      {isApexDosOutlet(outlet.name) ? (
        <Badge
          variant="info"
          data-testid={`apex-outlet-dos-${outlet.name}`}
          title="Apex DOS dosing pump (annotation only)"
        >
          DOS
        </Badge>
      ) : null}
      {safety === 'blocked' ? (
        <Badge
          variant="danger"
          data-testid={`apex-outlet-safety-${outlet.name}`}
          title="Life-support / chemistry hardware (display metadata only — this app cannot control outlets)"
        >
          LIFE SUPPORT
        </Badge>
      ) : null}
      <Badge variant={isOn ? 'ok' : 'neutral'} live={isOn}>
        {outlet.state}
      </Badge>
      <Badge variant={MODE_VARIANT[mode]}>{mode}</Badge>
      {intensityPct !== null ? (
        <div
          className="flex items-center gap-1.5"
          aria-label={`intensity ${intensityPct}%`}
          data-testid={`apex-outlet-intensity-${outlet.name}`}
        >
          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-reef-800/80">
            <div
              className="h-full rounded-full bg-reef-400"
              style={{ width: `${intensityPct}%` }}
            />
          </div>
          <span className="w-9 text-right tabular-nums text-[10px] text-reef-300">
            {intensityPct}%
          </span>
        </div>
      ) : null}
    </div>
  );
}
